import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PATH_SEGMENTS, route } from '../../config/paths';
import { listPublicDemos, DemoCatalogEntry } from '../../services/publicDemosClient';
import './LiveDemosSection.css';

const FEATURED_LIMIT = 3;

const LiveDemosSection: React.FC = () => {
  const [demos, setDemos] = useState<DemoCatalogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listPublicDemos();
        if (cancelled) return;
        const featured = rows.filter((d) => d.featured);
        const ordered = (featured.length ? featured : rows)
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .slice(0, FEATURED_LIMIT);
        setDemos(ordered);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load demos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="live-demos" className="live-demos-section">
      <div className="live-demos-container">
        <div className="live-demos-header">
          <div>
            <p className="live-demos-kicker">Proof, not slides</p>
            <h2 className="live-demos-title">Talk to a live voice agent</h2>
            <p className="live-demos-subtitle">
              Pick a workflow and speak — no signup required. Microphone access is used only for the
              demo session.
            </p>
          </div>
          <Link className="live-demos-all" to={route(PATH_SEGMENTS.LIVE_DEMO)}>
            See all demos →
          </Link>
        </div>

        {loading && <div className="live-demos-muted">Loading demos…</div>}
        {error && <div className="live-demos-error">{error}</div>}

        {!loading && !error && demos.length === 0 && (
          <div className="live-demos-muted">
            Demos are not available right now.{' '}
            <Link to={route(PATH_SEGMENTS.LIVE_DEMO)}>Check the gallery</Link>
          </div>
        )}

        <div className="live-demos-list">
          {demos.map((demo) => (
            <Link
              key={demo.id}
              className="live-demos-row"
              to={route(PATH_SEGMENTS.LIVE_DEMO, demo.id)}
            >
              <div className="live-demos-row-text">
                <span className="live-demos-row-industry">{demo.industry}</span>
                <h3 className="live-demos-row-title">{demo.title}</h3>
                <p className="live-demos-row-summary">{demo.summary}</p>
              </div>
              <span className="live-demos-row-cta">Talk now</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveDemosSection;
